/**
 * The staff console's shell (D39), its pure decisions: the console host (`console.<stage>…`, `config.json`'s
 * `backend.kind: 'console'`) serves the same build as the customer app with its own navigation — Tenants, Ops,
 * Releases, Rollouts, Announcements, Audit, Staff — and none of the product's pages. Who may be where:
 *
 *   - on the console, a user of the staff tenant (`GET /me`'s `tenant.staff`) gets the shell; anyone else is sent to
 *     the customer app (every `/vendor/*` route refuses them anyway);
 *   - on the customer app, a staff user is sent to the console (the API refuses the staff tenant every product write
 *     regardless, `@acp/api-contract/staffTenant.js`).
 *
 * Table-tested in `console.test.ts`, so the views only wire it.
 */
import { adminLanding, adminSections, ADMIN_HOME } from './admin/sections.js';
import type { Grants } from './permissions.js';
import type { Shell } from './runtimeConfig.js';

export type ConsoleSection = { readonly label: string; readonly href: string; readonly match: readonly string[] };

/** The console's top bar, in order. `match`: the path prefixes that mark the section current. */
export const CONSOLE_NAV: readonly ConsoleSection[] = [
  { label: 'Tenants', href: '/vendor/tenants', match: ['/vendor/tenants'] },
  { label: 'Ops', href: '/vendor/ops', match: ['/vendor/ops'] },
  { label: 'Releases', href: '/vendor/releases', match: ['/vendor/releases'] },
  // D40's rollout rings: phase 7f part B fills this section (`views/console/`).
  { label: 'Rollouts', href: '/vendor/rollouts', match: ['/vendor/rollouts'] },
  { label: 'Announcements', href: '/vendor/announcements', match: ['/vendor/announcements'] },
  { label: 'Audit', href: '/vendor/audit', match: ['/vendor/audit'] },
  // The staff tenant's own users, groups and invites: how a superadmin manages staff (the Admin area's sections).
  { label: 'Staff', href: '/admin/users', match: ['/admin'] },
];

/** Where the console lands. */
export const CONSOLE_HOME = '/vendor/tenants';

/** The Admin area's sections the console keeps (Staff): the staff tenant's users and groups — never sites, secrets or usage. */
export const CONSOLE_ADMIN_SECTIONS: readonly string[] = ['/admin/users', '/admin/groups'];

/** The console's pages besides its sections: my account (offline PIN is meaningless here, but the page is harmless) and who am I. */
const CONSOLE_EXTRA: readonly string[] = ['/account', '/whoami'];

function under(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/** Whether a page belongs to the console (anything else — a rundown, a studio — is the customer app's). */
export function consoleAllows(pathname: string): boolean {
  if (CONSOLE_EXTRA.some((p) => under(pathname, p))) return true;
  if (under(pathname, '/vendor')) return true;
  return pathname === '/admin' || CONSOLE_ADMIN_SECTIONS.some((p) => under(pathname, p));
}

/** The nav as the shell renders it: the current section marked. */
export function consoleNav(pathname: string): { readonly label: string; readonly href: string; readonly active: boolean }[] {
  return CONSOLE_NAV.map((s) => ({ label: s.label, href: s.href, active: s.match.some((m) => under(pathname, m)) }));
}

/**
 * What the signed-in layout shows, from the host's shell and whether the user is staff (`GET /me`'s `tenant.staff`;
 * null while unknown — not loaded yet, or a node, which serves no `/me`).
 *   - `shell`: the page's own shell (the customer app, or the console);
 *   - `use-console`: a staff user on the customer app;
 *   - `use-app`: a customer on the console;
 *   - `wait`: the console before `/me` answered (it shows nothing it might have to take back).
 */
export type HostVerdict = 'shell' | 'use-console' | 'use-app' | 'wait';

export function hostVerdict(shell: Shell, staff: boolean | null): HostVerdict {
  if (shell === 'console') {
    if (staff === null) return 'wait';
    return staff ? 'shell' : 'use-app';
  }
  return staff === true ? 'use-console' : 'shell';
}

/** The Admin area's sections a shell shows: all of the caller's in the customer app, only users and groups in the console. */
export function shellAdminSections<T extends { readonly href: string }>(sections: readonly T[], shell: Shell): T[] {
  return shell === 'console' ? sections.filter((s) => CONSOLE_ADMIN_SECTIONS.includes(s.href)) : [...sections];
}

/** Where `/admin` goes in a shell: its first section the caller may see, or the area itself. */
export function shellAdminLanding(grants: Grants, shell: Shell): string {
  if (shell === 'app') return adminLanding(grants);
  return shellAdminSections(adminSections(grants), shell)[0]?.href ?? ADMIN_HOME;
}

/** Whether `GET /me` said the caller is staff (`MeResult`): null while unknown — not loaded, or a node (no `/me`). */
export function staffOf(me: { readonly available: true; readonly me: { readonly tenant: { readonly staff?: boolean | undefined } } } | { readonly available: false } | undefined): boolean | null {
  if (me === undefined || !me.available) return null;
  return me.me.tenant.staff ?? false;
}
